'use client'

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { ShopLayout } from '@/components/shop/ShopLayout'
import { ProductGrid } from '@/components/shop/ProductGrid'
import { getProducts, getCategories, ProductListItem, Category } from '@/lib/api'

export default function HomePage() {
  const [products, setProducts] = useState<ProductListItem[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getCategories().then(setCategories).catch(() => setCategories([]))
  }, [])

  const loadProducts = useCallback(async (category: string | null) => {
    setLoading(true)
    setError(null)
    try {
      const data = await getProducts(category ? { category } : {})
      setProducts(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte ladda produkter')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProducts(activeCategory)
  }, [activeCategory, loadProducts])

  return (
    <ShopLayout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.06]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Handplockade produkter
            </span>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-neutral-900 dark:text-white leading-tight">
              Upptäck kvalitetsprodukter till bra priser
            </h1>
            <p className="mt-4 text-lg text-neutral-500 dark:text-neutral-400">
              Snabbt, enkelt och tryggt. Utforska hela sortimentet nedan.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#catalog" className="px-5 py-2.5 rounded-xl bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100">
                Handla nu
              </a>
              <Link href="/search" className="px-5 py-2.5 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 text-sm font-medium hover:bg-neutral-200 dark:hover:bg-neutral-700">
                Sök produkter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Catalog */}
      <section id="catalog" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white">Alla produkter</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              {loading ? 'Laddar…' : `${products.length} produkter`}
            </p>
          </div>
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeCategory === null
                ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
            }`}
          >
            Alla
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat.slug
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900'
                  : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {error ? (
          <div className="py-16 text-center text-red-500">{error}</div>
        ) : loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-neutral-100 dark:border-neutral-800 overflow-hidden animate-pulse">
                <div className="aspect-square bg-neutral-100 dark:bg-neutral-800" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded w-3/4" />
                  <div className="h-3 bg-neutral-100 dark:bg-neutral-800 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <ProductGrid products={products} emptyMessage="Inga produkter i den här kategorin ännu." />
        )}
      </section>
    </ShopLayout>
  )
}
